<template>
  <div>
    <template v-if="isOnboardingRedesign">
      <h2 class="question-title with-subtext">
        Great news! You’re eligible for treatment.
      </h2>
      <p class="sub-text group-mb tw-mt-4">
        We’re legally required to collect the following information in order to provide you medical care. Enter your full legal name exactly as it appears on your government-issued ID.
      </p>
    </template>
    <template v-else>
      <h2 class="question-title with-subtext">
        Ok great! You are eligible for treatment.
      </h2>
      <p class="sub-text group-mb">
        Please get started with your doctor visit by creating an account below.
        All the information you provide is legally required to provide medical
        care.
      </p>
    </template>
    <b-row>
      <b-col
        cols="12"
        :lg="isOnboardingRedesign ? 12 : 6"
      >
        <div
          role="group"
          class="group-mb"
        >
          <label
            v-if="!isOnboardingRedesign"
            for="customerfn"
            class="input-label"
          >Legal First Name*</label>
          <input
            id="customerfn"
            v-model="firstname"
            name="customerfn"
            type="text"
            class="fs-mask form-control onboarding-input"
            :class="getFieldClass(firstname, checkFirstNameValidity)"
            :placeholder="isOnboardingRedesign ? 'Legal First Name *' : ''"
            required
          >
          <b-form-invalid-feedback
            id="input-live-feedback"
            :state="checkFirstNameValidity"
          >
            First name must be alphanumeric
          </b-form-invalid-feedback>
        </div>
      </b-col>

      <b-col
        cols="12"
        :lg="isOnboardingRedesign ? 12 : 6"
      >
        <div
          role="group"
          class="group-mb"
        >
          <label
            v-if="!isOnboardingRedesign"
            for="customerln"
            class="input-label"
          >Legal Last Name*</label>
          <input
            id="customerln"
            v-model="lastname"
            name="customerln"
            type="text"
            class="fs-mask form-control onboarding-input"
            :class="getFieldClass(lastname, checkLastNameValidity)"
            :placeholder="isOnboardingRedesign ? 'Legal Last Name *' : ''"
            required
          >
          <b-form-invalid-feedback
            id="input-live-feedback"
            :state="checkLastNameValidity"
          >
            Last name must be alphanumeric
          </b-form-invalid-feedback>
        </div>
      </b-col>

      <b-col
        cols="12"
        :lg="isOnboardingRedesign ? 12 : 6"
      >
        <div
          role="group"
          class="group-mb"
        >
          <label
            v-if="!isOnboardingRedesign"
            for="birthday"
            class="input-label"
          >Birthday*</label>
          <b-form-input
            id="birthday"
            v-model="birthdayDisplay"
            class="fs-mask onboarding-input"
            :class="{'tw-text-lg': isOnboardingRedesign}"
            required
            readonly
            :placeholder="isOnboardingRedesign ? 'Birthday *' : ''"
          />
          <hr
            v-if="isOnboardingRedesign"
            class="tw-mt-[0.5rem]"
          >
        </div>
      </b-col>

      <b-col
        cols="12"
        :lg="isOnboardingRedesign ? 12 : 6"
      >
        <div
          role="group"
          class="group-mb"
        >
          <label
            v-if="!isOnboardingRedesign"
            for="state"
            class="input-label"
          >
            State*
          </label>

          <b-form-input
            id="state"
            v-model="stateName"
            class="fs-mask onboarding-input"
            :class="{'tw-text-lg': isOnboardingRedesign}"
            required
            readonly
            :placeholder="isOnboardingRedesign ? 'State *' : ''"
          />
          <hr
            v-if="isOnboardingRedesign"
            class="tw-mt-[0.5rem]"
          >
        </div>
      </b-col>

      <b-col cols="12">
        <div
          role="group"
          class="group-mb"
        >
          <label
            v-if="!isOnboardingRedesign"
            for="phoneno"
            class="input-label"
          >Phone Number*</label>
          <b-form-input
            id="phoneno"
            v-model="phoneno"
            class="fs-mask onboarding-input hide-arrows"
            :class="{'tw-text-lg': isOnboardingRedesign}"
            type="tel"
            pattern="[0-9]*"
            :formatter="formatPhoneNumber"
            required
            :placeholder="isOnboardingRedesign ? 'Phone Number *' : ''"
            @keyup.enter="submitAnswer"
          />
        </div>
      </b-col>

      <b-col
        v-if="$cookies.get('referralToken')"
        cols="12"
      >
        <div
          role="group"
          class="group-mb"
        >
          <label
            for="refcode"
            class="input-label"
          >Referral Code</label>
          <b-form-input
            id="refcode"
            v-model="refcode"
            class="fs-mask onboarding-input"
            readonly
          />
        </div>
      </b-col>

      <b-col
        v-if="isOnboardingRedesign"
        cols="12"
      >
        <div class="tw-pt-2 tw-flex tw-items-center tw-gap-2">
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.97266 14.2422C6.02018 14.2422 5.1224 14.0599 4.2793 13.6953C3.44076 13.3307 2.7002 12.8271 2.05762 12.1846C1.41504 11.542 0.911458 10.8014 0.546875 9.96289C0.182292 9.11979 0 8.22201 0 7.26953C0 6.31706 0.182292 5.42155 0.546875 4.58301C0.911458 3.73991 1.41276 2.99707 2.05078 2.35449C2.69336 1.71191 3.43392 1.20833 4.27246 0.84375C5.11556 0.479167 6.01335 0.296875 6.96582 0.296875C7.91829 0.296875 8.81608 0.479167 9.65918 0.84375C10.5023 1.20833 11.2451 1.71191 11.8877 2.35449C12.5303 2.99707 13.0339 3.73991 13.3984 4.58301C13.763 5.42155 13.9453 6.31706 13.9453 7.26953C13.9453 8.22201 13.763 9.11979 13.3984 9.96289C13.0339 10.8014 12.5303 11.542 11.8877 12.1846C11.2451 12.8271 10.5023 13.3307 9.65918 13.6953C8.82064 14.0599 7.92513 14.2422 6.97266 14.2422ZM6.97266 13.0801C7.7793 13.0801 8.53353 12.9297 9.23535 12.6289C9.93717 12.3281 10.5547 11.9134 11.0879 11.3848C11.6211 10.8516 12.0358 10.234 12.332 9.53223C12.6328 8.8304 12.7832 8.07617 12.7832 7.26953C12.7832 6.46289 12.6328 5.70866 12.332 5.00684C12.0312 4.30501 11.6143 3.6875 11.0811 3.1543C10.5524 2.62109 9.9349 2.20638 9.22852 1.91016C8.52669 1.60938 7.77246 1.45898 6.96582 1.45898C6.15918 1.45898 5.40495 1.60938 4.70312 1.91016C4.0013 2.20638 3.38607 2.62109 2.85742 3.1543C2.32878 3.6875 1.91406 4.30501 1.61328 5.00684C1.31706 5.70866 1.16895 6.46289 1.16895 7.26953C1.16895 8.07617 1.31706 8.8304 1.61328 9.53223C1.91406 10.234 2.32878 10.8516 2.85742 11.3848C3.39062 11.9134 4.00814 12.3281 4.70996 12.6289C5.41178 12.9297 6.16602 13.0801 6.97266 13.0801ZM6.96582 8.5C6.61491 8.5 6.4349 8.31999 6.42578 7.95996L6.33691 4.34375C6.33236 4.17057 6.38704 4.02702 6.50098 3.91309C6.61947 3.79915 6.77214 3.74219 6.95898 3.74219C7.14128 3.74219 7.29167 3.80143 7.41016 3.91992C7.5332 4.03385 7.59245 4.17741 7.58789 4.35059L7.48535 7.95996C7.48079 8.31999 7.30762 8.5 6.96582 8.5ZM6.96582 10.7217C6.7653 10.7217 6.59212 10.6533 6.44629 10.5166C6.30046 10.3753 6.22754 10.2067 6.22754 10.0107C6.22754 9.81478 6.30046 9.64844 6.44629 9.51172C6.59212 9.37044 6.7653 9.2998 6.96582 9.2998C7.16634 9.2998 7.33952 9.36816 7.48535 9.50488C7.63119 9.6416 7.7041 9.81022 7.7041 10.0107C7.7041 10.2113 7.62891 10.3799 7.47852 10.5166C7.33268 10.6533 7.16178 10.7217 6.96582 10.7217Z"
              fill="#1A6A72"
            />
          </svg>
          <div>
            Fields marked with<span class="tw-text-[#1A6A72] tw-px-0.5">*</span>are required.
          </div>
        </div>
      </b-col>
      <b-col v-if="isOnboardingRedesign">
        <slot name="error"></slot>
      </b-col>
      <b-col
        v-if="isOnboardingRedesign"
        cols="12"
        class=" tw-mt-3"
      >
        <b-form-checkbox
          v-model="optforpromotions"
          class="mb-2 checkbox-label"
          :value="true"
          :unchecked-value="false"
        >
          Yes, I'd like to occasionally receive Winona promotions via text.
          <div class="tw-flex tw-flex-col tw-gap-3 tw-mt-2">
            <small
              class="text-muted"
            >
              By checking the box above, you agree to receive automated promotional messages. This agreement is not a condition of purchase, and message frequency may vary. Message and data rates may apply. Reply STOP to opt-out or HELP for assistance. For more information, see our <a
                href="javascript:;"
                @click="redirectToSite('privacy-policy')"
              >Privacy Policy</a> or <a
                href="javascript:;"
                @click="redirectToSite('contact')"
              >Contact Us</a>.
            </small>
          </div>
        </b-form-checkbox>
      </b-col>

      <b-col
        v-else
        cols="12"
      >
        <b-form-checkbox
          v-model="optforpromotions"
          class="mb-2 checkbox-label"
          :value="true"
          :unchecked-value="false"
        >
          Yes, I'd like to occasionally receive Winona promotions via text
        </b-form-checkbox>
      </b-col>

      <b-col
        v-if="!isOnboardingRedesign"
        cols="12"
        class="mt-4"
      >
        <div class="tw-flex tw-flex-col tw-gap-3">
          <small
            class="text-muted"
            style="font-size: 8pt"
          >
            By checking the above box, you agree to receive automated
            promotional messages. This agreement is not a condition of purchase.
            Message frequency varies. Reply STOP to opt-out or HELP for help.
            Message & data rates apply. For questions, please
            <a
              href="javascript:;"
              @click="redirectToSite('contact')"
            >contact us.
            </a>
            <a
              href="javascript:;"
              @click="redirectToSite('privacy-policy')"
            >Privacy Policy.</a>
          </small>
        </div>
      </b-col>
    </b-row>

    <div class="tw-flex tw-flex-col tw-gap-2 tw-mt-2 tw-justify-center">
      <small :class="{'tw-text-center tw-max-w-[230px] tw-mx-auto sm:tw-max-w-full tw-mt-4': isOnboardingRedesign}">
        <svg
          v-if="isOnboardingRedesign"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="115.71 176.187 9.33 13.405"
          width="9.33px"
          height="13.405px"
          class="tw-inline-block tw-mr-1"
        ><path
          id="object-0"
          d="M 117.24 189.592 C 116.74 189.592 116.36 189.455 116.1 189.182 C 115.84 188.913 115.71 188.507 115.71 187.965 L 115.71 183.255 C 115.71 182.717 115.84 182.314 116.1 182.045 C 116.36 181.772 116.74 181.635 117.24 181.635 L 123.5 181.635 C 124 181.635 124.38 181.772 124.64 182.045 C 124.91 182.314 125.04 182.717 125.04 183.255 L 125.04 187.965 C 125.04 188.507 124.91 188.913 124.64 189.182 C 124.38 189.455 124 189.592 123.5 189.592 L 117.24 189.592 Z M 117.28 188.56 L 123.47 188.56 C 123.62 188.56 123.73 188.514 123.81 188.423 C 123.89 188.336 123.93 188.209 123.93 188.04 L 123.93 183.18 C 123.93 183.011 123.89 182.884 123.81 182.797 C 123.73 182.71 123.62 182.667 123.47 182.667 L 117.28 182.667 C 117.13 182.667 117.02 182.71 116.94 182.797 C 116.85 182.884 116.81 183.011 116.81 183.18 L 116.81 188.04 C 116.81 188.209 116.85 188.336 116.94 188.423 C 117.02 188.514 117.13 188.56 117.28 188.56 Z M 116.9 182.161 L 116.9 180.001 C 116.9 179.144 117.07 178.433 117.39 177.868 C 117.73 177.303 118.16 176.882 118.69 176.604 C 119.22 176.326 119.78 176.187 120.37 176.187 C 120.97 176.187 121.53 176.326 122.06 176.604 C 122.59 176.882 123.01 177.303 123.34 177.868 C 123.67 178.433 123.84 179.144 123.84 180.001 L 123.84 182.161 L 122.76 182.161 L 122.76 179.858 C 122.76 179.283 122.65 178.8 122.43 178.408 C 122.21 178.016 121.91 177.72 121.55 177.52 C 121.19 177.319 120.8 177.219 120.37 177.219 C 119.94 177.219 119.55 177.319 119.19 177.52 C 118.82 177.72 118.54 178.016 118.32 178.408 C 118.1 178.8 117.99 179.283 117.99 179.858 L 117.99 182.161 L 116.9 182.161 Z"
          fill="#1A1A1A"
        /></svg>
        <b-icon
          v-else
          icon="lock-fill"
        />
        Any information you submit is
        encrypted within our secure platform.
      </small>

      <img
        :src="isOnboardingRedesign ? require(`@/assets/images/cro/hsa-fsa-eligible-badge.svg`) : require(`@/assets/images/cro/hsa-fsa-eligible-badge.png`)"
        alt=""
        class="tw-mx-auto"
        :class="isOnboardingRedesign ? 'tw-h-[30px]' : 'tw-h-[18px]'"
      >
    </div>

    <OnboardingCTAContainer>
      <div class="tw-w-full tw-flex tw-flex-col tw-gap-2 tw-text-center">
        <b-button
          pill
          block
          variant="info"
          class="slight-rounded"
          :class="{'tw-max-w-[300px] tw-mx-auto tw-text-base md:tw-text-lg': isOnboardingRedesign}"
          :disabled="isOnboardingRedesign ? (isFieldsEmpty || buttonDisabled) : buttonDisabled"
          @click="submitAnswer"
        >
          {{ isOnboardingRedesign ? 'Continue' : 'Start my free visit' }}
        </b-button>
      </div>
    </OnboardingCTAContainer>
  </div>
</template>

<script>
import { mapState } from 'vuex'
// components
import OnboardingCTAContainer from '@/components/onboarding/OnboardingCTAContainer';

// services
import { OnboardingService } from '@/services/onboarding.service';

// mixins
import formValidation from '@/components/form/mixins';
import { fsaHsaPromoMixin } from '@/mixins/fsaHsaPromo';

// libs
import Vue from 'vue';

export default {
  components: {
    OnboardingCTAContainer,
  },
  mixins: [formValidation, fsaHsaPromoMixin],
  props: {
    eligibilityParams: {
      type: Object,
      default: () => ({})
    },
    registrationParams: {
      type: Object,
      default: () => ({})
    },
    stateName: {
      type: String,
      default: ''
    },
    email: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      firstname: '',
      lastname: '',
      buttonDisabled: false,
      phoneno: '',
      optforpromotions: true,
    };
  },
  computed: {
    ...mapState('global', ['isOnboardingRedesign']),
    getFieldClass() {
      return (fieldValue, isValid) => {
        let isOnboardingRedesignClass = '';
        let validClass = '';
        if (this.isOnboardingRedesign) {
          isOnboardingRedesignClass = 'tw-text-lg';
        }
        if (fieldValue.length > 0) {
          validClass = isValid ? 'is-valid' : 'is-invalid';
        }
        return `${validClass} ${isOnboardingRedesignClass}`.trim();
      };
    },

    refcode() {
      if ($cookies.get('referralToken')) {
        return $cookies.get('referralToken');
      } else {
        return '';
      }
    },
    checkFirstNameValidity() {
      return this.firstname == ''
        ? null
        : /^[\w\-\s]+$/.test(this.firstname)
          ? true
          : false;
    },
    checkLastNameValidity() {
      return this.lastname == ''
        ? null
        : /^[\w\-\s]+$/.test(this.lastname)
          ? true
          : false;
    },
    birthdayDisplay() {
      if (this.eligibilityParams.birthday) {
        const birthdayArray = this.eligibilityParams.birthday.split('-');
        return `${birthdayArray[1]}-${birthdayArray[2]}-${birthdayArray[0]}`;
      } else {
        return '';
      }
    },
    isFieldsEmpty() {
      return !this.firstname || !this.lastname || !this.phoneno;
    },
  },
  watch: {
    firstname() {
      this.$emit('update-registration', 'firstname', this.firstname);
    },
    lastname() {
      this.$emit('update-registration', 'lastname', this.lastname);
    },
    phoneno() {
      this.$emit('update-registration', 'phoneno', this.phoneno);
    },
  },
  async mounted () {
    window.scrollTo(0, 0);
    if (this.registrationParams) {
      this.firstname = this.registrationParams.firstname;
      this.lastname = this.registrationParams.lastname;
      this.phoneno = this.registrationParams.phoneno;
    }
  },
  methods: {
    redirectToSite(path) {
      console.log(`${process.env.VUE_APP_WEBSITE_URL}/${path}`);
      window.location.href = `${process.env.VUE_APP_WEBSITE_URL}/${path}`;
    },
    async submitAnswer() {
      this.buttonDisabled = true;

      // Check discount code
      try {
        if ($cookies.get('promocode')) {
          await OnboardingService.checkCoupon(
            $cookies.get('promocode')
          );
        }
      } catch (err) {
        this.$emit('showAlert', err.message);
        this.buttonDisabled = false;
        return;
      }

      if (!this.firstname || !this.lastname || !this.phoneno) {
        this.$emit('showAlert', 'Please fill in all fields.');
        this.buttonDisabled = false;
      } else if (this.phoneno.length < 12){
        this.$emit('showAlert', "We can't seem to process that phone number. Please make sure it follows the format 234-567-8910.");
        this.buttonDisabled = false;
      } else if (
        !/^[\w\-\s]+$/.test(this.firstname) ||
        !/^[\w\-\s]+$/.test(this.lastname)
      ) {
        this.$emit(
          'showAlert',
          'First name and last name must be alphanumeric characters only.'
        );
        this.buttonDisabled = false;
      } else {
        const data = {
          phoneno: this.phoneno,
          optforpromotions: this.optforpromotions,
        };

        if (this.firstname) {
          //data.firstname = this.firstname;
          Vue.set(data, 'firstname', this.firstname);
        }
        if (this.lastname) {
          //data.lastname = this.lastname;
          Vue.set(data, 'lastname', this.lastname);
        }

        for (const key in this.eligibilityParams) {
          //if (!data[key]) data[key] = this.eligibilityParams[key];
          if (!data[key]) Vue.set(data, key, this.eligibilityParams[key]);
        }

        if ('ovariansurgery' in data && !data.ovariansurgery) {
          delete data.ovariansurgery;
        }

        try {
          this.$emit('finish', data);
          this.$_fsaHsaPromo_setSessionBannerShownToPatient();
        } catch (err) {
          if (
            err.message == 'An account with the given email already exists.'
          ) {
            this.$emit(
              'showAlert',
              `An account with the email ${this.email} already exists. You may sign in from the link below or enter a new email address to create a new account.`
            );
          } else {
            if (err.status == 500) {
              this.$store.dispatch('sendErrorLog', {
                step: 'Eligibility',
                error_log: {
                  message: err,
                },
              });
            }
            this.$emit('showAlert', err.message);
          }
          this.buttonDisabled = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.hide-arrows::-webkit-outer-spin-button,
.hide-arrows::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"], .hide-arrows {
  -moz-appearance: textfield;
  appearance: textfield;
}

.redesign {
  .onboarding-input{
    padding-left: 1rem !important;
    padding-right: 1rem;
  }
  .onboarding-input:read-only {
    background-color: #f3f3f3;
    margin-top: 4px;
    cursor: not-allowed;
    border:0;
    border-radius: 8px;
    padding-top: 0;
    padding-bottom: 0;
    height: 32px;
  }
}
</style>
